import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  FileSpreadsheet,
  PenLine,
  MessageCircle,
  Upload,
  Loader2,
  Plus,
  X,
  Download,
  CheckCircle2,
} from "lucide-react";
import useReveal from "../shared/useReveal";
import {
  normalizeManualHoldings,
  parsePortfolioFile,
} from "../../utils/portfolio/parseHoldings";
import { analyzePortfolio } from "../../utils/portfolio/analyzePortfolio";
import "./PortfolioTeaserHome.css";
import "./HomeSections.css";

const WA_URL =
  "https://wa.me/919784862667?text=" +
  encodeURIComponent(
    "Hi Poonji Mitra, I'd like a free portfolio review. I'll share my holdings."
  );

const UPLOAD_OPTS = [
  { id: "cams", label: "CAMS Statement", icon: FileText, accept: ".pdf,.xlsx,.xls,.csv,.txt" },
  { id: "kfintech", label: "KFintech Statement", icon: FileText, accept: ".pdf,.xlsx,.xls,.csv,.txt" },
  { id: "pdf", label: "PDF Upload", icon: FileText, accept: ".pdf,.txt" },
  { id: "excel", label: "Excel / CSV", icon: FileSpreadsheet, accept: ".xlsx,.xls,.csv" },
  { id: "manual", label: "Manual Entry", icon: PenLine },
  { id: "whatsapp", label: "WhatsApp It to Us", icon: MessageCircle },
];

const SAMPLE = {
  healthScore: 72,
  riskScore: 81,
  riskLabel: "High",
  diversification: 58,
  metrics: [
    { v: "14.2%", l: "XIRR" },
    { v: "12.8%", l: "CAGR" },
    { v: "+1.9%", l: "vs Benchmark" },
    { v: "68%", l: "Equity" },
    { v: "22%", l: "Debt" },
    { v: "10%", l: "Gold/Other" },
  ],
  recs: [
    "Reduce concentrated sector risk",
    "Rebalance to target asset allocation",
    "Switch to better-performing funds",
    "Tax-saving & capital-gains harvesting ideas",
  ],
};

const emptyRow = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  fundName: "",
  amount: "",
  units: "",
  invested: "",
});

function riskWord(score) {
  if (score >= 75) return "High";
  if (score >= 55) return "Moderate";
  if (score >= 35) return "Balanced";
  return "Low";
}

function formatPct(n, signed = false) {
  if (n == null || Number.isNaN(n)) return "—";
  const v = Math.round(n * 10) / 10;
  const s = `${v}%`;
  return signed && v > 0 ? `+${s}` : s;
}

function downloadSampleCsv() {
  const csv = [
    "Fund Name,Current Value,Units,Invested Value",
    "HDFC Flexi Cap Fund - Direct Growth,250000,120.5,180000",
    "SBI Magnum Midcap Fund - Direct Growth,120000,85.2,95000",
    "ICICI Prudential Liquid Fund - Direct Growth,80000,450.1,78000",
    "Nippon India ETF Gold BeES,35000,55,30000",
    "Parag Parikh Flexi Cap Fund - Direct Growth,150000,90.3,110000",
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "poonji-portfolio-sample.csv";
  a.click();
  URL.revokeObjectURL(url);
}

export default function PortfolioTeaserHome() {
  const ref = useReveal();
  const fileRef = useRef(null);
  const [selected, setSelected] = useState("cams");
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([emptyRow()]);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [animateBars, setAnimateBars] = useState(false);

  const opt = UPLOAD_OPTS.find((o) => o.id === selected) || UPLOAD_OPTS[0];
  const isManual = selected === "manual";
  const isWhatsApp = selected === "whatsapp";

  useEffect(() => {
    const t = requestAnimationFrame(() => setAnimateBars(true));
    return () => cancelAnimationFrame(t);
  }, [result]);

  const pickOption = (id) => {
    if (id === "whatsapp") {
      window.open(WA_URL, "_blank", "noopener,noreferrer");
      return;
    }
    setSelected(id);
    setError("");
    if (id === "manual") setFile(null);
  };

  const onFile = useCallback((f) => {
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) {
      setError("File is too large. Please upload under 12 MB.");
      return;
    }
    setFile(f);
    setError("");
    setResult(null);
  }, []);

  const canAnalyze = isManual
    ? rows.some((r) => r.fundName.trim() && Number(r.amount) > 0)
    : Boolean(file);

  const runAnalyze = async () => {
    if (!canAnalyze || analyzing) return;
    setAnalyzing(true);
    setError("");
    setAnimateBars(false);

    try {
      let holdings = [];
      let warnings = [];

      if (isManual) {
        holdings = normalizeManualHoldings(rows);
      } else {
        const parsed = await parsePortfolioFile(file);
        holdings = parsed.holdings;
        warnings = parsed.warnings || [];
      }

      if (!holdings.length) {
        throw new Error(
          "No holdings found. Try Excel/CSV with Fund Name + Current Value columns, or enter funds manually."
        );
      }

      await new Promise((r) => setTimeout(r, 450));

      const analysis = analyzePortfolio(holdings, {
        fileName: file?.name,
        source: selected,
        warnings,
      });
      setResult(analysis);
      window.setTimeout(() => {
        document.getElementById("home-pr-report")?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 80);
    } catch (err) {
      setResult(null);
      setError(err?.message || "Could not analyse this portfolio.");
    } finally {
      setAnalyzing(false);
    }
  };

  const health = result?.healthScore ?? SAMPLE.healthScore;
  const risk = result?.riskScore ?? SAMPLE.riskScore;
  const divers = result?.diversification ?? SAMPLE.diversification;
  const riskLabel = result ? riskWord(result.riskScore) : SAMPLE.riskLabel;

  const equityPct = result?.assets?.find((a) => a.label === "Equity")?.pct;
  const debtPct = result?.assets?.find((a) => a.label === "Debt")?.pct;
  const otherPct = result
    ? Math.round(
        (100 -
          (equityPct || 0) -
          (debtPct || 0)) *
          10
      ) / 10
    : null;

  const benchDelta =
    result?.benchmarks?.[0] != null
      ? result.benchmarks[0].you - result.benchmarks[0].bench
      : null;

  const metrics = result
    ? [
        { v: formatPct(result.xirr), l: "Est. XIRR" },
        { v: formatPct(result.cagr), l: "Est. CAGR" },
        { v: formatPct(benchDelta, true), l: "vs Benchmark" },
        { v: formatPct(equityPct ?? 0), l: "Equity" },
        { v: formatPct(debtPct ?? 0), l: "Debt" },
        { v: formatPct(otherPct ?? 0), l: "Gold/Other" },
      ]
    : SAMPLE.metrics;

  const recs = result
    ? result.recommendations.slice(0, 4).map((r) => r.title)
    : SAMPLE.recs;

  return (
    <section className="section alt" id="portfolio-review-teaser">
      <div className="container" ref={ref}>
        <div className="home-section-head section-head pm-reveal">
          <span className="kicker">Free Portfolio Review</span>
          <h2>Is your portfolio working as hard as you are?</h2>
          <p className="sub">
            Upload your holdings and get an instant health check — risk, returns,
            allocation and clear next steps. Files stay on your device.
          </p>
        </div>

        <div className="home-pr-grid">
          <div className="home-pr-card pm-reveal">
            <h3>1 · Share your portfolio</h3>

            <div className="home-upload-opts" role="group" aria-label="Upload options">
              {UPLOAD_OPTS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  className={`home-upload-opt${selected === id && !isWhatsApp ? " sel" : ""}`}
                  onClick={() => pickOption(id)}
                >
                  <Icon size={15} strokeWidth={2.2} aria-hidden />
                  <span>{label}</span>
                </button>
              ))}
            </div>

            <input
              ref={fileRef}
              type="file"
              className="home-pr-file"
              accept={opt.accept || ".pdf,.xlsx,.xls,.csv,.txt"}
              onChange={(e) => onFile(e.target.files?.[0])}
            />

            {!isManual && !isWhatsApp && (
              <div
                className={`home-pr-drop${file ? " has-file" : ""}`}
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  onFile(e.dataTransfer.files?.[0]);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") fileRef.current?.click();
                }}
              >
                <Upload size={20} />
                {file ? (
                  <div>
                    <strong>{file.name}</strong>
                    <span>{(file.size / 1024).toFixed(1)} KB · click to replace</span>
                  </div>
                ) : (
                  <div>
                    <strong>Drop {opt.label} here</strong>
                    <span>or click to browse · PDF / Excel / CSV · max 12 MB</span>
                  </div>
                )}
              </div>
            )}

            {isManual && (
              <div className="home-pr-manual">
                {rows.map((row) => (
                  <div className="home-pr-manual-row" key={row.id}>
                    <input
                      type="text"
                      placeholder="Fund name"
                      value={row.fundName}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((r) =>
                            r.id === row.id ? { ...r, fundName: e.target.value } : r
                          )
                        )
                      }
                    />
                    <input
                      type="number"
                      min="0"
                      placeholder="Value ₹"
                      value={row.amount}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((r) =>
                            r.id === row.id ? { ...r, amount: e.target.value } : r
                          )
                        )
                      }
                    />
                    <button
                      type="button"
                      className="home-pr-manual-x"
                      onClick={() =>
                        setRows((prev) =>
                          prev.length <= 1 ? prev : prev.filter((r) => r.id !== row.id)
                        )
                      }
                      aria-label="Remove fund"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="home-pr-add"
                  onClick={() => setRows((prev) => [...prev, emptyRow()])}
                >
                  <Plus size={14} /> Add fund
                </button>
              </div>
            )}

            {error && <p className="home-pr-error">{error}</p>}

            <div className="home-pr-actions">
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: "100%" }}
                disabled={!canAnalyze || analyzing}
                onClick={runAnalyze}
              >
                {analyzing ? (
                  <>
                    <Loader2 size={16} className="home-pr-spin" />
                    Analysing…
                  </>
                ) : (
                  "Analyse Portfolio"
                )}
              </button>
              <button
                type="button"
                className="home-pr-sample"
                onClick={downloadSampleCsv}
              >
                <Download size={14} /> Sample CSV
              </button>
            </div>

            <p className="home-pr-note">
              Your statements stay on this device and are used only for analysis.
            </p>
          </div>

          <div className="home-pr-card pm-reveal" id="home-pr-report">
            <h3>
              2 · {result ? "Your report" : "What your report covers"}{" "}
              {!result && (
                <span className="home-pr-sample-tag">(sample)</span>
              )}
              {result && (
                <span className="home-pr-live-tag">
                  <CheckCircle2 size={12} /> Live
                </span>
              )}
            </h3>

            {result?.totalValue != null && (
              <p className="home-pr-total">
                Portfolio value{" "}
                <b>₹{Number(result.totalValue).toLocaleString("en-IN")}</b>
                {result.meta?.holdingsCount
                  ? ` · ${result.meta.holdingsCount} holdings`
                  : ""}
              </p>
            )}

            <div className="home-scorebar">
              <div className="home-sb-top">
                <span>Portfolio Health Score</span>
                <b>
                  {health} / 100
                  {result?.healthLabel ? ` · ${result.healthLabel}` : ""}
                </b>
              </div>
              <div className="home-track">
                <div
                  className="home-fill"
                  style={{ width: animateBars ? `${health}%` : 0 }}
                />
              </div>
            </div>
            <div className="home-scorebar">
              <div className="home-sb-top">
                <span>Risk Score</span>
                <b>
                  {result ? `${risk} · ${riskLabel}` : riskLabel}
                </b>
              </div>
              <div className="home-track">
                <div
                  className="home-fill"
                  style={{ width: animateBars ? `${risk}%` : 0 }}
                />
              </div>
            </div>
            <div className="home-scorebar">
              <div className="home-sb-top">
                <span>Diversification Score</span>
                <b>{divers} / 100</b>
              </div>
              <div className="home-track">
                <div
                  className="home-fill"
                  style={{ width: animateBars ? `${divers}%` : 0 }}
                />
              </div>
            </div>

            <div className="home-pr-metrics">
              {metrics.map((m) => (
                <div className="home-pr-metric" key={m.l}>
                  <b>{m.v}</b>
                  <span>{m.l}</span>
                </div>
              ))}
            </div>

            <ul className="home-pr-recs">
              {recs.map((text) => (
                <li key={text}>{text}</li>
              ))}
            </ul>

            {result && (
              <Link to="/portfolio-review" className="home-pr-full">
                Open full analysis →
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
